import React, { useEffect, useRef, useState } from 'react';

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updatePreference);
      return () => mediaQuery.removeEventListener('change', updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  return prefersReducedMotion;
}

function formatValue(value, decimals, decimalSeparator, padStart, number) {
  let formatted = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();

  if (decimals > 0) {
    formatted = formatted.replace('.', decimalSeparator);
  }

  if (padStart) {
    const finalIntLength = Math.floor(Math.abs(number)).toString().length;
    const [intPart, fracPart] = formatted.split(decimalSeparator);
    const paddedInt = intPart?.padStart(finalIntLength, '0') ?? '';
    return fracPart ? `${paddedInt}${decimalSeparator}${fracPart}` : paddedInt;
  }

  return formatted;
}

export function CountingNumber({
  number,
  fromNumber = 0,
  padStart = false,
  inView = false,
  inViewMargin = '0px',
  inViewOnce = true,
  decimalSeparator = '.',
  decimalPlaces,
  className = '',
  ...props
}) {
  const localRef = useRef(null);
  const observerRef = useRef(null);
  const frameRef = useRef(0);
  const hasAnimatedRef = useRef(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const numberStr = number.toString();
  const decimals =
    typeof decimalPlaces === 'number'
      ? decimalPlaces
      : numberStr.includes('.')
        ? numberStr.split('.')[1]?.length ?? 0
        : 0;
  const finalIntLength = Math.floor(Math.abs(number)).toString().length;
  const initialText = padStart
    ? '0'.padStart(finalIntLength, '0') + (decimals > 0 ? decimalSeparator + '0'.repeat(decimals) : '')
    : `0${decimals > 0 ? decimalSeparator + '0'.repeat(decimals) : ''}`;
  const [displayText, setDisplayText] = useState(initialText);

  useEffect(() => {
    if (!localRef.current) {
      return undefined;
    }

    const finalText = formatValue(number, decimals, decimalSeparator, padStart, number);

    const updateValue = (value) => {
      setDisplayText(formatValue(value, decimals, decimalSeparator, padStart, number));
    };

    const animate = () => {
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
      }

      if (prefersReducedMotion) {
        updateValue(number);
        hasAnimatedRef.current = true;
        return;
      }

      const duration = 1400;
      const start = performance.now();
      const delta = number - fromNumber;

      const step = (timestamp) => {
        const elapsed = timestamp - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        updateValue(fromNumber + delta * eased);

        if (progress < 1) {
          frameRef.current = window.requestAnimationFrame(step);
        } else {
          hasAnimatedRef.current = true;
        }
      };

      frameRef.current = window.requestAnimationFrame(step);
    };

    if (inViewOnce && hasAnimatedRef.current) {
      setDisplayText(finalText);
      return undefined;
    }

    const runWhenVisible = () => {
      if (inView && observerRef.current) {
        return;
      }

      if (!inView) {
        animate();
        return;
      }

      if (typeof IntersectionObserver !== 'function') {
        animate();
        return;
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            if (inViewOnce && hasAnimatedRef.current) {
              return;
            }

            animate();

            if (inViewOnce && observerRef.current) {
              observerRef.current.disconnect();
              observerRef.current = null;
            }
          });
        },
        {
          rootMargin: inViewMargin,
          threshold: 0.2,
        }
      );

      observerRef.current.observe(localRef.current);
    };

    setDisplayText(initialText);
    runWhenVisible();

    return () => {
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [decimalSeparator, decimals, fromNumber, inView, inViewMargin, inViewOnce, initialText, number, padStart, prefersReducedMotion]);

  return (
    <span className={className} data-slot='counting-number' ref={localRef} {...props}>
      {displayText}
    </span>
  );
}

export default CountingNumber;
